import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { appActions } from 'store/app/slice';
import { patientsActions } from 'store/patients/slice';
import { File, FileType, filesArrType } from 'types';
import { createMessageReqBody } from 'utils';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 mb

export const handleFilesSelected = async (
  plainFiles: globalThis.File[],
  patientCaseID: number,
  dispatch: ThunkDispatch<unknown, undefined, UnknownAction>,
) => {
  const files: filesArrType = [];

  await Promise.all(
    plainFiles.map(async (newUploadedFile) => {
      if (newUploadedFile.size > MAX_FILE_SIZE) {
        dispatch(appActions.setIsModalOpened({ name: 'attachmentErrorModal', isOpened: true }));
        return;
      }

      const localUrl = URL.createObjectURL(newUploadedFile);
      const splitFilename = newUploadedFile.name.split('.');
      const fileName =
        splitFilename.length === 2 ? splitFilename[0] : splitFilename.slice(0, splitFilename.length - 1).join('.');
      const fileExtension = splitFilename[splitFilename.length - 1].toLowerCase();

      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(newUploadedFile);

      const fileData = await new Promise<ArrayBuffer>((resolve, reject) => {
        fileReader.addEventListener('loadend', (e) => {
          resolve(fileReader.result as ArrayBuffer);
        });
        fileReader.addEventListener('error', (e) => {
          reject(fileReader.error);
        });
      });

      const newFile: File & { data: ArrayBuffer } = {
        id: String(newUploadedFile.lastModified),
        name: fileName,
        type: newUploadedFile.type.split('/')[1]?.toUpperCase() as FileType,
        size: newUploadedFile.size,
        url: localUrl,
        data: fileData,
        extension: fileExtension,
      };
      files.push(newFile);
    }),
  ).catch((error: any) => {
    dispatch(appActions.setModalErrorText({ name: 'attachmentError', errorText: error?.message }));
    dispatch(appActions.setIsModalOpened({ name: 'attachmentErrorModal', isOpened: true }));
  });

  try {
    const wrappedResult = dispatch(patientsActions.uploadMessageFiles(files));
    const { filesInfo } = await wrappedResult.unwrap();

    const messageReqBodies = filesInfo
      .map((el) => createMessageReqBody(el))
      .map((el) => {
        return dispatch(
          patientsActions.sendPatientMessage({
            messageData: el,
            patientCaseId: patientCaseID,
          }),
        );
      });

    await Promise.all(messageReqBodies);

    dispatch(patientsActions.getPatientMessages({ patientCaseId: patientCaseID, patientMessageParams: {} }));
    dispatch(patientsActions.setPatientMessageFilters({}));
  } catch {
    dispatch(appActions.setIsModalOpened({ name: 'attachmentErrorModal', isOpened: true }));
  }
};
